import React, { useEffect } from "react";
import { MapPin, Phone, Mail } from "lucide-react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import Hero from "../components/home/Hero";
import Services from "../components/home/Services";
import WhyChooseUs from "../components/home/WhyChooseUs";
import ContactForm from "../components/home/ContactForm";
import notificationSound from "../assets/image/notification.mp3";
import { WhatsAppChat } from "../components/common/WhatsAppChat";

const Home = () => {
  const cities = [
    { name: "Melbourne", state: "VIC" },
    { name: "Perth", state: "WA" },
    { name: "Adelaide", state: "SA" },
  ];

  useEffect(() => {
    const audio = new Audio(notificationSound);
    const timer = setTimeout(() => {
      audio.play().catch((err) => console.log("Audio play failed:", err));
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="relative">
      <Helmet>
        <title>Professional Cleaning Services in Australia | EnviPro</title>
        <meta
          name="description"
          content="EnviPro offers professional cleaning services for homes and businesses across Melbourne, Perth, and Adelaide. Get a free quote today!"
        />
        <meta
          name="keywords"
          content="cleaning services, professional cleaning, commercial cleaning, residential cleaning, Melbourne, Perth, Adelaide"
        />
        <meta
          property="og:title"
          content="Professional Cleaning Services in Australia | EnviPro"
        />
        <meta
          property="og:description"
          content="Expert cleaning solutions for homes and businesses across major Australian cities."
        />
        <link rel="canonical" href="info@enviprosolutions.com.au" />
      </Helmet>

      {/* Rest of the component remains exactly the same */}
      <Hero />
      <Services />
      <WhyChooseUs />
      <WhatsAppChat />

      <div className="relative bg-gradient-to-b from-gray-50 to-white">
        <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-transparent" />

        <div className="relative max-w-7xl mx-auto py-20 px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-bold text-gray-900">
                Need Professional Cleaning Services?
              </h2>
              <p className="mt-6 text-lg text-gray-600">
                Contact us today for a free quote. We provide customized
                cleaning solutions for businesses and homes across Australia.
              </p>

              <div className="mt-12">
                <div className="space-y-4">
                  <motion.div
                    whileHover={{ x: 10 }}
                    className="flex items-center space-x-3 group"
                  >
                    <Phone className="h-5 w-5 text-secondary" />
                    <span className="text-gray-700 hover:text-secondary transition-colors duration-300">
                      Mob : 0452298814 Tel : 0882708801
                    </span>
                  </motion.div>
                  <motion.div
                    whileHover={{ x: 10 }}
                    className="flex items-center space-x-3 group"
                  >
                    <Mail className="h-5 w-5 text-secondary" />
                    <span className="text-gray-700 hover:text-secondary transition-colors duration-300">
                      info@enviprosolutions.com.au
                    </span>
                  </motion.div>
                </div>

                <div className="mt-12">
                  <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                    <MapPin className="h-6 w-6 text-secondary mr-2" />
                    Our Service Areas
                  </h3>
                  <div className="mt-6 grid grid-cols-2 gap-6">
                    {cities.map((city) => (
                      <motion.div
                        key={city.name}
                        whileHover={{ x: 10 }}
                        className="flex items-center group"
                      >
                        <div className="h-2 w-2 bg-secondary rounded-full group-hover:scale-150 transition-all duration-300" />
                        <span className="ml-3 text-gray-600">
                          {city.name}
                          <span className="text-gray-400 text-sm ml-1">
                            {city.state}
                          </span>
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:pl-8"
            >
              <ContactForm />
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
